import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Section, HeadingItem } from '@atlaskit/menu';
import { Stack, Inline, Text } from "@atlaskit/primitives/compiled";
import Heading from '@atlaskit/heading';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';

import { MultiSpaceSelector } from './MultiSpaceSelector.tsx';
import { SelectedSpacesTable } from './SelectedSpacesTable.tsx';

const i18n = defineMessages({
	exclusionTitle: {
		id: 'bulk-permissions.space.space-exclusion-panel-content.exclusion-title.non-final',
		defaultMessage: 'Spaces to exclude',
		description: 'title for the space exclusion panel drawer',
	},
	inclusionTitle: {
		id: 'bulk-permissions.space.space-exclusion-panel-content.inclusion-title.non-final',
		defaultMessage: 'Spaces to include',
		description: 'title for the space exclusion panel drawer',
	},
	closeButtonTooltip: {
		id: 'bulk-permissions.space.space-exclusion-panel-content.close-tooltip.non-final',
		defaultMessage: 'Close panel',
		description: 'tooltip that appears upon hover for the close button',
	},
	exclusionDescription: {
		id: 'bulk-permissions.space.space-exclusion-panel-content.exclusion-description.non-final',
		defaultMessage: 'Add which spaces should be excluded from this bulk access action.',
		description: 'exclusion description for the space exclusion panel',
	},
	inclusionDescription: {
		id: 'bulk-permissions.space.space-exclusion-panel-content.inclusion-description.non-final',
		defaultMessage: 'Add which spaces should be included in this bulk access action.',
		description: 'inclusion description for the space exclusion panel',
	},
	maximum: {
		id: 'bulk-permissions.space.space-exclusion-panel-content.maximum.non-final',
		defaultMessage: 'Maximum of 50.',
		description:
			'indicator of the maximum amount of spaces one can select to exclude from a bulk access action',
	},
});

const styles = cssMap({
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingBottom: token('space.100'),
	},
	body: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
});

export const SpaceSelectionPanelContent = ({
	onClose,
	isExclusion,
}: {
	onClose: () => void;
	isExclusion: boolean;
}) => {
	const { formatMessage } = useIntl();

	return (
		<>
			<Stack testId="space-selection-right-side-panel">
				<Section>
					<HeadingItem data-testId="space-selection-panel-header">
						<Inline xcss={styles.header}>
							<Inline>
								<SpacesIcon label="space selection icon" spacing="spacious" />
								<Heading size="small">
									{formatMessage(isExclusion ? i18n.exclusionTitle : i18n.inclusionTitle)}
								</Heading>
							</Inline>

							<IconButton icon={CrossIcon} label="closeButton" onClick={onClose} />
						</Inline>
					</HeadingItem>
				</Section>
				<Section hasSeparator>
					<Stack xcss={styles.body} space="space.200">
						<Text>
							{formatMessage(isExclusion ? i18n.exclusionDescription : i18n.inclusionDescription)}
						</Text>
						<Text>{formatMessage(i18n.maximum)}</Text>
						<MultiSpaceSelector />
						<SelectedSpacesTable />
					</Stack>
				</Section>
			</Stack>
		</>
	);
};
